module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('p', {
    staticClass: "CN",
    attrs: {
      "id": "ch001"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_18"
    }
  }), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch-001"
    }
  }, [_vm._v("Chapter 1")])]), _vm._v(" "), _c('p', {
    staticClass: "CT"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch-001"
    }
  }, [_vm._v("Assessing the Sources")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush"
  }, [_c('span', {
    staticClass: "dropcap"
  }, [_vm._v("I")]), _vm._v("n contrast to Jesus and James of Jerusalem, who have at least minimal attestation from extracanonical sources,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn1",
      "id": "fn1"
    }
  }, [_vm._v("1")])]), _vm._v(" Paul is not mentioned by any ancient outsider source, a fact that reminds us that we ought not to confuse Paul’s canonical dominance and subsequent influence with the impact he had in his own time."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn2",
      "id": "fn2"
    }
  }, [_vm._v("2")])]), _vm._v(" There are, however, multiple sources for Paul outside the canonical writings that deserve attention, if only to clear the way for a closer consideration of the sources in the New Testament. Besides providing useful ground-clearing, a cursory look at extracanonical sources also establishes background for a discussion of the critical issues concerning the New Testament Acts and Letters.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The case of James, in fact, offers a helpful point of reference for analyzing the extracanonical sources concerning Paul. In the New Testament, James appears as the author of a letter, briefly in the narrative of Acts (12:17; 15:13–21; 21:18–25), and passingly in Paul’s letters (1 Cor 15:7; Gal 1:19; 2:9–12). But a substantial amount of lore concerning James developed in later Christian circles. Some was straightforwardly encomiastic, such as the several accounts of James’s martyrdom."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn3",
      "id": "fn3"
    }
  }, [_vm._v("3")])]), _vm._v(" But some was also plainly ideological, "), _c('a', {
    attrs: {
      "id": "page_20"
    }
  }, [_vm._v("advancing a particular vision of Christianity through its depiction of James. One ecclesiastically centered stream of tradition, for example, portrays James as the first bishop of Jerusalem;"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn4",
      "id": "fn4"
    }
  }, [_vm._v("4")]), _vm._v(" a set of gnostic compositions sees him as a privileged recipient of revelations;"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn5",
      "id": "fn5"
    }
  }, [_vm._v("5")])]), _vm._v(" and in the pseudo-Clementine literature, James appears as the champion of a law-observant Christianity, whose opponent is a certain Simon, possibly a stand-in for Paul, described as an opponent of the law and therefore of the Christianity supposedly represented by James."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn6",
      "id": "fn6"
    }
  }, [_vm._v("6")])]), _vm._v(" None of these traditions, remarkably, seem to have had the least connection with the canonical letter ascribed to James.")]), _vm._v(" "), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s1"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s1"
    }
  }, [_vm._v("Traditions about Paul")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("The earliest—and particularly precious—testimonies concerning Paul come from Clement of Rome (ca. 95), Ignatius of Antioch (ca. 110), and Polycarp of Smyrna (between 110 and 140). They provide evidence for the circulation of Paul’s letters and “fill in gaps” of his life and death. Clement writes to the church in Corinth that Peter and Paul together are “righteous pillars of the church [who] were persecuted and contended unto death” (1 Clem. 5.2). Concerning Paul in particular, he writes, “Through jealousy and strife Paul showed the way to the prize of endurance; seven times he was in bond, he was exiled, he was stoned, he was a herald both in the East and in the West, he gained the noble fame of his faith, he taught righteousness in all the world, and when he had reached the limits of the West he gave his testimony before the rulers, and thus passed from the world and was taken up into the Holy Place—the greatest example of endurance” (5.5–7)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn7",
      "id": "fn7"
    }
  }, [_vm._v("7")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Clement also refers explicitly to 1 Corinthians, telling his readers to “take up the epistle of the blessed Paul the Apostle,” which Paul wrote to them "), _c('a', {
    attrs: {
      "id": "page_21"
    }
  }, [_vm._v("“at the beginning of his preaching,” and displays knowledge of the letter’s content (1 Clem. 47.1–4)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn8",
      "id": "fn8"
    }
  }, [_vm._v("8")]), _vm._v(" If the conventional dating of 1 Clement is accepted, this letter from an elder in Rome testifies to the spread of at least one of Paul’s letters to that city within about forty years of its composition and about thirty years of Paul’s probable death. More fascinating, it takes Paul’s journey to Spain and second defense as something of which the Corinthians would be aware. The nonchalance of the notice suggests that such an oral tradition had circulated before Clement’s time, and if the tradition was based on guesswork from Romans and 2 Timothy, it supports the position that these letters were already known and associated with Paul.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Ignatius of Antioch mentions Paul twice. In "), _c('i', [_vm._v("To the Ephesians")]), _vm._v(" 12.2, he speaks of martyrs and would-be martyrs like himself as “fellow initiates with Paul, who was sanctified, who gained a good report, who was right blessed, in whose footsteps may I be found when I attain to God, who in every epistle makes mention of you in Christ Jesus.” As in Clement, Paul is praised for his character and witness. The reference to Paul mentioning the Ephesians in “every epistle” is clearly erroneous if we take the entire canonical collection into account, but it suggests that Ignatius had some group of Paul’s letters in which Ephesus is in fact mentioned."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn9",
      "id": "fn9"
    }
  }, [_vm._v("9")])]), _vm._v(" In "), _c('i', [_vm._v("To the Romans")]), _vm._v(" 4.3, Ignatius urges the church against preventing his martyrdom, stating, “I do not order you as did Peter and Paul; they were apostles, I am a convict; they were free, I am even until now a slave.” We note here the similarity to the contrast that Clement had drawn between Paul’s generation and his own (1 Clem. 47.4–5).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Sometime before the middle of the second century, Polycarp of Smyrna had been asked by the Philippian church to send them a collection of Ignatius of Antioch’s letters. Polycarp composed a missive of his own to accompany that collection, "), _c('i', [_vm._v("To the Philippians")]), _vm._v(", the entire set of compositions delivered by Polycarp’s delegate, Crescens (Pol. "), _c('i', [_vm._v("Phil.")]), _vm._v(" 14.1). Polycarp speaks of Paul as a model of endurance together with “the other apostles” (Pol. "), _c('i', [_vm._v("Phil.")]), _vm._v(" 9.1), and alludes to Paul’s praise of the Philippians “in the beginning of his epistle” to them (Pol. "), _c('i', [_vm._v("Phil.")]), _vm._v(" 11.3). "), _c('a', {
    attrs: {
      "id": "page_22"
    }
  }, [_vm._v("But Polycarp also characterizes Paul’s ministry and provides a florilegium of Pauline texts. He states, “I write to you concerning righteousness, not at my own instance, but because you first invited me. For neither am I, nor is any other like me, able to follow the wisdom of the blessed and glorious Paul, who when he was with you in the presence of the men of that time taught accurately and steadfastly the word of truth, and also when he was absent wrote letters to you, from the study of which you will be able to build yourselves up into the faith given you” (Pol. "), _c('i', [_vm._v("Phil.")]), _vm._v(" 3.1–2)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn10",
      "id": "fn10"
    }
  }, [_vm._v("10")]), _vm._v(" Polycarp proceeds to weave together a set of moral instructions, which he regards as coming from Paul’s letters, and in which we can detect loose citations or allusions to many of them (Galatians, Romans, Philippians, 1 and 2 Corinthians), including ones that scholars dispute as authentic (2 Thessalonians, Colossians, Ephesians, 1 and 2 Timothy). Only 1 Thessalonians, Philemon, and Titus are not represented.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The use of Paul changes in the middle of the second century, when his letters are appropriated by Marcion of Sinope (ca. 85–160) as the basis for what he claimed was the only true form of Christianity,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn11",
      "id": "fn11"
    }
  }, [_vm._v("11")])]), _vm._v(" using all of Paul’s letters—except for the Pastorals, which he rejected"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn12",
      "id": "fn12"
    }
  }, [_vm._v("12")])]), _vm._v("—against the Old Testament (sponsored by the evil creator god) and the “Judaizing” elements of the New Testament, a dialectical stance illustrated by his "), _c('i', [_vm._v("Antitheses")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn13",
      "id": "fn13"
    }
  }, [_vm._v("13")])]), _vm._v(" In his rebuttal of Marcion, Tertullian (ca. 212) plays the good lawyer and carefully refrains from employing the Pastoral Letters that Marcion rejects, but otherwise draws from all the other letters in the canon; and in his other writings, Tertullian uses the Pastoral Letters as well, considering them authentically Pauline. Similarly, in his massive response to Marcion and the gnostics—among Valentinians, in particular, Paul was popular"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn14",
      "id": "fn14"
    }
  }, [_vm._v("14")])]), _vm._v("—Irenaeus of Lyons (ca. 180) employs all the letters attributed to Paul in the canon, including the Pastorals, omitting only Philemon."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn15",
      "id": "fn15"
    }
  }, [_vm._v("15")])]), _vm._v(" These exchanges are the beginning of a long history of theological debate in which Paul’s canonical letters play a central and controverted role. However fascinating that history is to follow, our present interest is in traditions about Paul himself as sources of knowledge concerning the apostle and his writings.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_vm._v("Before turning to apocryphal writings attached to Paul, then, we can quickly note further positive statements. The second-century (?) Epistle of the Apostles has the risen Jesus instructing the apostles about the conversion and ministry of Paul."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn16",
      "id": "fn16"
    }
  }, [_vm._v("16")]), _vm._v(" The second-century Acts of Peter repeats the belief we saw already in 1 Clement that Paul preached in Spain before a second trial before Nero."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn17",
      "id": "fn17"
    }
  }, [_vm._v("17")])]), _vm._v(" Clement of Alexandria (ca. 215) concludes from Paul’s reference to “my true companion” (Phil 4:3) that he was married."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn18",
      "id": "fn18"
    }
  }, [_vm._v("18")])]), _vm._v(" Ambrosiaster (late fourth century) argued Paul worked harder than any other apostle because he practiced his trade as well as preached."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn19",
      "id": "fn19"
    }
  }, [_vm._v("19")])]), _vm._v(" John Chrysostom (ca. 390) devoted a series of encomiastic homilies to Paul as the exemplar of virtue."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn20",
      "id": "fn20"
    }
  }, [_vm._v("20")])]), _vm._v(" Jerome can appropriately conclude this list with his entry on Paul in his "), _c('i', [_vm._v("On Illustrious Men")]), _vm._v(" (late fourth century), which once more states that Paul preached in the West before having a second trial and being beheaded under Nero in the year 68; Jerome lists all thirteen of Paul’s letters, carefully distinguishing the Letter to the Hebrews on the grounds of “style and language,” supposing it to be the work of Barnabas, or Luke the Evangelist, or Clement of Rome. He notes that “some read one also to the Laodiceans, but it is rejected by everyone.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn21",
      "id": "fn21"
    }
  }, [_vm._v("21")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("As I noted earlier, however, not everyone praised Paul. He was criticized by some Hellenistic critics of Christianity,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn22",
      "id": "fn22"
    }
  }, [_vm._v("22")])]), _vm._v(" but even more severely by Jewish Christians who saw him as “the enemy” whose liberating message for gentiles was perceived as inimical to their own law-observant version of the gospel. It is important to emphasize, despite some scholarly opinion, that the canonical Letter of James should not be read as an anti-Pauline composition."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn23",
      "id": "fn23"
    }
  }, [_vm._v("23")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("Epiphanius (ca. 375) reports on the Ebionites that they “slander Paul, using certain charges trumped up by the malice and error of their pseudo-apostles.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn24",
      "id": "fn24"
    }
  }, [_vm._v("24")]), _vm._v(" By far the most important (and most difficult to disentangle) Jewish-Christian hostility to Paul is expressed in the so-called Pseudo-Clementine literature, which is a complex set of novelistic compositions from sometime before the fourth century"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn25",
      "id": "fn25"
    }
  }, [_vm._v("25")])]), _vm._v(" but possibly containing earlier sources."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn26",
      "id": "fn26"
    }
  }, [_vm._v("26")])]), _vm._v(" Among such hypothetical earlier sources is The Ascents of James, found within the "), _c('i', [_vm._v("Recognitions")]), _vm._v("."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn27",
      "id": "fn27"
    }
  }, [_vm._v("27")])]), _vm._v(" It pictures James as leading a debate with Jewish leaders and almost winning them over, before “the hostile man”—later identified as Paul—disrupts the meeting and almost kills James by throwing him from the steps of the temple."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn28",
      "id": "fn28"
    }
  }, [_vm._v("28")])]), _vm._v(" There are also sections of the Pseudo-Clementine "), _c('i', [_vm._v("Homilies")]), _vm._v(" that contain Peter’s polemic against “Simon Magus,” which some scholars take to be a veiled attack on Paul."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn29",
      "id": "fn29"
    }
  }, [_vm._v("29")])]), _vm._v(" With the Pseudo-Clementine writings, however, we find ourselves entering into another category of sources for Paul—namely, apocryphal literature.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Before turning attention to the Acts and Apocalypses and letters that seek to portray Paul—all more positively than the hostile witnesses I have just reviewed—through such acts of imaginative art, it is helpful to review what has been learned from the first set of witnesses surveyed. First, apart from his martyrdom, the earliest traditions about Paul are largely dependent on the canonical letters; even the modest “filling in of the gaps” (such as Paul having a wife, or working long hours, or preaching in Spain) are drawn by inference from hints in his letters, and the more extravagant portrayal of opposition between Peter (and James) and Paul could derive from an ideologically driven reading of the letters. Second, with the exception of his champion Marcion on one side and his enemies the Jewish Christians on the other, Paul is less noted as a theologian than as a moral teacher and model of endurance (see above all Polycarp). Third, again with the exception of Marcion, "), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }, [_vm._v("these sources affirm the canonical collection, including the Pastorals, while denying the Pauline authorship of Hebrews on the grounds of style and rejecting the Letter to the Laodiceans altogether. In short, the sources so far considered build on but do not significantly add to what is stated or implied by the canonical sources.")])]), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s2"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s2"
    }
  }, [_vm._v("Apocryphal Compositions")])]), _vm._v(" "), _c('p', {
    staticClass: "H2",
    attrs: {
      "id": "s3"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s3"
    }
  }, [_vm._v("Apocalypses")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Two extant compositions called “apocalypses” feature Paul as one who ascends the heavenly realms through a visionary experience. Each is plainly based on Paul’s own account in 2 Cor 12:1–4 of having gone up into the third heaven—“snatched into paradise”—and heard words he was not able to express."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn30",
      "id": "fn30"
    }
  }, [_vm._v("30")])]), _vm._v(" The first is the "), _c('i', [_vm._v("Visio Pauli")]), _vm._v(" or Apocalypse of Paul, probably composed in the mid-third century."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn31",
      "id": "fn31"
    }
  }, [_vm._v("31")])]), _vm._v(" Paul plays the passive role of seer. The descriptions of the fate awaiting those who had died had a significant impact on later descriptions of the afterlife,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn32",
      "id": "fn32"
    }
  }, [_vm._v("32")])]), _vm._v(" but the composition adds nothing helpful to our store of knowledge about Paul. The second is the much shorter Revelation of Paul in Coptic found in the Nag Hammadi codices (V, 2)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn33",
      "id": "fn33"
    }
  }, [_vm._v("33")])]), _vm._v(" This vision also depicts the fates of sinners but has Paul ascend to the tenth heaven, where he is at the end united with the twelve apostles. Like the "), _c('i', [_vm._v("Visio Pauli")]), _vm._v(", the gnostic apocalypse fills in the gap opened by Paul’s allusive account in 2 Cor 12:1–4.")]), _vm._v(" "), _c('p', {
    staticClass: "H2",
    attrs: {
      "id": "s4"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s4"
    }
  }, [_vm._v("Acts")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("In the second and third centuries, narrative accounts of several apostles were produced, among the earliest being the Acts of Paul."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn34",
      "id": "fn34"
    }
  }, [_vm._v("34")])]), _vm._v(" The three largest "), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_vm._v("coherent sections of the composition are (1) the Acts of Paul and Thecla, which circulated independently in association with the cult of the female saint; (2) a Corinthian Correspondence, containing a letter from the Corinthians and an answering letter by Paul (more on which below); and (3) the Martyrdom of Paul, which also circulated independently. The remainder of the material in the Acts of Paul relates Paul’s travels and wonders, from Damascus to Rome."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn35",
      "id": "fn35"
    }
  }, [_vm._v("35")]), _vm._v(" Like the other narratives about apostles composed in the same time frame, the Acts of Paul owes something to the precedent set by the canonical Acts of the Apostles, but more closely resembles the Greco-Roman novels that were popular in the same period."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn36",
      "id": "fn36"
    }
  }, [_vm._v("36")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The narrative has a number of distinctive features in addition to providing a physical description of Paul (Acts of Paul 3)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn37",
      "id": "fn37"
    }
  }, [_vm._v("37")])]), _vm._v(" As in the canonical Acts, Paul is a wonder-worker: he has visions, heals the sick, exorcizes demons, escapes prison, baptizes a lion, raises from the dead; at his beheading, milk spills on the executioner’s tunic. Paul preaches in the form of beatitudes that emphasize the purity of the body: “Blessed are the bodies of the virgins, for they shall be well pleasing to God, and shall not lose the reward of their chastity” (Acts of Paul 6). He is portrayed as a disrupter of households, precisely because his message of continence leads young women to forsake marriage (Acts of Paul 9, 12, 15). The narrative is striking most of all for the positive and powerful role given to Thecla, who is bold and courageous (Acts of Paul 25–28, 33, 37), baptizes herself, cuts her hair, dresses like a man, and declares her intention to preach; only at the end does Paul respond to her, “Go and preach the word of God” (Acts of Paul 40–41).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The Acts of Paul most surprises by its apparent familiarity with the Pastoral Letters. The place name Iconium and the character names Onesiphorus, Demas, Hermogenes the coppersmith, and Titus recall especially 2 Timothy, as does the strange echo of 2 Tim 2:18 in the declaration of Demas and Hermogenes that “we shall teach you about the resurrection which he says is to come, that it has already taken place in the children and that we rise again, having come to the knowledge of the true God” (Acts of Paul 14).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_vm._v("The Acts of Paul reverses the position in the Pastorals: here the ascetical Paul subverts traditional marriage and the domestic order, whereas the Paul of the Pastorals affirms both (1 Tim 2:15; 4:3–4; 5:14; Titus 2:2–5). The difference is so striking that some scholars propose that the Pastorals and Acts of Paul represent an ideological battle within a later Pauline tradition concerning the role of women in the church: Should they be active and preach, or stay within the household?"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn38",
      "id": "fn38"
    }
  }, [_vm._v("38")]), _vm._v(" This is not the place to rehearse those arguments."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn39",
      "id": "fn39"
    }
  }, [_vm._v("39")])]), _vm._v(" Certainly the Paul of the “undisputed” letters is ambiguous enough on the role of women, but postulating a direct conflict between the Pastorals and the Acts of Paul on a single subject oversimplifies."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn40",
      "id": "fn40"
    }
  }, [_vm._v("40")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("More pertinent to the present survey is the status of the Acts of Paul as a source for the life or thought of the apostle. Its overall novelistic character, its hagiographical portrayal of Thecla, its failure to convey even an echo of the concerns of Paul found in the canonical letters, its lack of genuine knowledge concerning Judaism or Paul’s Jewish context, its romantic portrayal of the politics of the Roman Empire, and its clumsy effort to imitate Paul’s correspondence all locate the Acts of Paul and Thecla as an act of imagination that has only the loosest connection to historical realities. The same is true of three other apocryphal writings associated with Paul composed in the fifth century or later: the Acts of Timothy,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn41",
      "id": "fn41"
    }
  }, [_vm._v("41")])]), _vm._v(" the Acts of Titus,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn42",
      "id": "fn42"
    }
  }, [_vm._v("42")])]), _vm._v(" and the Epistle of Pseudo-Dionysius the Areopagite to Timothy Concerning the Deaths of the Apostles Peter and Paul."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn43",
      "id": "fn43"
    }
  }, [_vm._v("43")])])]), _vm._v(" "), _c('p', {
    staticClass: "H2",
    attrs: {
      "id": "s5"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s5"
    }
  }, [_vm._v("Letters")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("There are actually fewer extracanonical letters ascribed to Paul than one might expect. In the Acts of Paul, which had earlier offered a sample of Paul’s preaching that sounds more like Jesus (6), we also find a letter from the Corinthian community to Paul and a letter from Paul to the Corinthians (designated 3 Corinthians). It has a few echoes of Pauline phrases recognizable from the canonical collection: “become alive by adoption” (8), “not children of righteousness but of wrath” (19), "), _c('a', {
    attrs: {
      "id": "page_28"
    }
  }, [_vm._v("“how much more will he raise you up, who have believed in Christ Jesus, as he himself was raised up” (31), “let no man trouble me” (34), “I bear his marks” (36). But the bulk of the letter contains language and ideas reflective of a later period. The purported letter, after all, takes the form of answering questions posed by the Corinthians concerning doctrinal points some are denying. Thus, Paul is asked to address whether Jesus came in the flesh and was born of Mary (14), and Paul responds, “Our Lord Jesus Christ was born of Mary of the seed of David, the Father having sent the spirit from heaven into her” (6). Discussing the resurrection, the letter uses the analogy of seeds (as Paul does in 1 Cor 15:38) but combines it with the example of Jonah and the whale (found in Matt 12:39–41). In short, the letter offers few reasons to take it seriously as a production of Paul himself, and many reasons to regard it as a composition of a significantly later date (late second century?) by someone having some rough awareness of Pauline diction.")])]), _c('a', {
    attrs: {
      "id": "page_28"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_28"
    }
  }, [_vm._v("A classic example of an apocryphal writing seeking to “fill the gaps” in canonical compositions is the Epistle to the Laodiceans."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn44",
      "id": "fn44"
    }
  }, [_vm._v("44")]), _vm._v(" In Col 4:16 Paul makes reference to a letter he wrote to the Laodiceans, wanting the Colossian community and the Laodiceans to exchange letters. It is not in the canonical collection, so the way was open for some pious admirer of Paul to supply the want. The extant text may be from as early as the second century (the Muratorian Canon mentions such a letter). In contrast to 3 Corinthians, this composition reveals genuine knowledge of some of Paul’s letters and weaves together Pauline phrases drawn especially from Galatians and Philippians."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn45",
      "id": "fn45"
    }
  }, [_vm._v("45")])]), _vm._v(" It consequently has no distinctive voice of its own—there is no discernible argument—and its entire goal is expressed in its last line, “And see that (this epistle) is read to the Colossians and that of the Colossians to you” (20). Being in effect a florilegium drawn from Paul’s letters, it adds nothing to our knowledge of the apostle.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Finally, the charming set of compositions known as the Correspondence of Paul and Seneca"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn46",
      "id": "fn46"
    }
  }, [_vm._v("46")])]), _vm._v(" exploits the opening provided by the facts that Paul and the Roman philosopher were contemporaries, both wrote letters, and both had contact with the emperor Nero. Eight of the short letters—actually notes—are from Seneca to the apostle, and six from Paul to the philosopher. Probably composed in the fourth century, the fictional letters presuppose a Pauline corpus: Seneca tells Paul, “I enjoyed reading your letters to the Galatians, to the "), _c('a', {
    attrs: {
      "id": "page_29"
    }
  }, [_vm._v("Corinthians, and to the Achaeans” (7); Paul notes, “I ought, as I have often claimed, to be all things to all men” (10). The philosopher sees Paul as having a superior wisdom (2, 7, 12), but criticizes his style of writing: “I do wish you would obey me and comply with the pure Latin style” (13). The apostle in turn encourages Seneca to preach to the imperial court: “You must make of yourself a new herald of Jesus Christ by displaying with the praises of rhetoric that blameless wisdom which you have almost achieved” (14). Taken as a whole, the correspondence seeks to demonstrate the superiority of the Christian way by having one of Rome’s most respected philosophers pay it court."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn47",
      "id": "fn47"
    }
  }, [_vm._v("47")])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("A few summary remarks on the apocryphal Pauline literature are appropriate. First, both external (textual) evidence and internal references within these compositions place them between the middle of the second century to the fifth century or even later. Second, the two apocalypses in which Paul figures as visionary are both based on the account of Paul’s ascent in 2 Cor 12:1–4. Third, the Acts of Paul is a novelistic account (much like the other Acts of the apostles) that tells a completely different story than does canonical Acts; it ignores completely any Jewish context for Paul’s life; it fails to give any sense of the content of his preaching or teaching found in the canonical sources; and it lacks any of the historical verisimilitude (and partial verifiability) of the canonical Acts. Finally, the apocryphal letters ascribed to Paul are—with the partial exception of Laodiceans—stylistically at a great distance from the canonical letters; they evince no engagement with local circumstances or persons in the manner of all the canonical letters apart from Ephesians; they lack any Jewish context or employment of Scripture; and, apart from the “fill the gaps” piety of Laodiceans, they reflect significantly later concerns, such as Christianity’s relationship to pagan philosophy and correct doctrine concerning the incarnation and Mary.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Scholars are quite right, then, to dismiss the apocryphal writings as important sources for Paul’s life and thought. In taking the time to assess these sources, however, we remind ourselves of an important point: the acceptance or rejection of any composition is a matter of judgment and the careful application of criteria. It ought not to be a matter of taste or, worse, majority opinion that guides such judgment. In the case of the sources we have been surveying, such judgment is fairly easy and enjoys universal approval. But confirming a judgment that a source is spurious rather than authentic (as with the apocryphal letters) still requires something more than lazy assent. The point becomes more pertinent as we turn to the canonical sources.")]), _vm._v(" "), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s6"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_30"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s6"
    }
  }, [_vm._v("Canonical Sources")])]), _vm._v(" "), _c('p', {
    staticClass: "H2",
    attrs: {
      "id": "s7"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s7"
    }
  }, [_vm._v("The Acts of the Apostles")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("The two-volume composition commonly referred to as Luke-Acts consists of the Gospel of Luke and the Acts of the Apostles."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn48",
      "id": "fn48"
    }
  }, [_vm._v("48")])]), _vm._v(" They are written by the same author, conventionally designated as the evangelist Luke, and together form a literary whole, a narrative that stretches from the hill country of Galilee to the city of Rome, from the birth of Jesus to the captivity of Paul, across a period of decades. The story of Jesus in the Gospel anticipates the account of the church in Acts, and the account of Jesus’s prophetic successors in Acts constantly echoes the portrayal of Jesus as the prophet who brings God’s visitation to the people. The narrative of Luke-Acts works out a theological argument concerning on one side God’s extension of salvation to the gentiles and on the other side God’s fidelity to his promises to Israel."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn49",
      "id": "fn49"
    }
  }, [_vm._v("49")])]), _vm._v(" It is within this overall argument that the portrayal of Paul appears in Acts 8:1–9:30; 11:25–30; 13:1–28:31; like Jesus and the other apostles, Paul is portrayed in prophetic terms: he is filled with the Holy Spirit, he proclaims the Word of God, he works signs and wonders, and he creates responses of both acceptance and rejection."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn50",
      "id": "fn50"
    }
  }, [_vm._v("50")])]), _vm._v(" The very nature of Luke’s overall presentation therefore has Paul play a role that is coordinated with that of other witnesses, and in continuity with the work of Jesus himself.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Such attention given to the gentile mission and to Christianity’s relationship with Judaism immediately sets Luke-Acts apart from the apocryphal Pauline literature, where neither concern is evident, and makes plausible a dating of the composition within decades of the death of the apostle. The literary unity of Acts and Luke’s Gospel points to a single author, and given the conventional dating of the Gospel of Luke around 85 CE, a similar date for Acts is not unreasonable."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn51",
      "id": "fn51"
    }
  }, [_vm._v("51")])]), _vm._v(" The prologue to the Gospel (1:1–4) presents the entire work as a form of history: the author knows of earlier narratives, has used sources, and has done his own research. Allowing for the "), _c('i', [_vm._v("kind")]), _vm._v(" of history Luke writes—a form of apologetic for the Christian movement, for "), _c('a', {
    attrs: {
      "id": "page_31"
    }
  }, [_vm._v("Paul, and above all for the faithfulness of God"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn52",
      "id": "fn52"
    }
  }, [_vm._v("52")]), _vm._v("—there is every reason to regard it as no less historical in character than the similarly interested writings of Josephus."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn53",
      "id": "fn53"
    }
  }, [_vm._v("53")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("As for Luke’s sources, we are able to detect his use of Mark and Q in the Gospel part of his narrative, simply because we have Mark and Matthew for comparison; if they were not extant, we would not be able to find them within Luke’s seamless prose. In the case of Acts, the quest for specific sources employed by Luke has proven mostly frustrating."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn54",
      "id": "fn54"
    }
  }, [_vm._v("54")])]), _vm._v(" The first eight chapters of Acts consist of a handful of specific incidents given a sense of amplitude through the generous use of speeches and summaries. The parts of Acts dealing with Paul have more detail and circumstantial detail, suggesting the use (at least) of an itinerary as a source."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn55",
      "id": "fn55"
    }
  }, [_vm._v("55")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The truly critical question is whether Luke knew or used Paul’s letters in the composition of the Acts narrative. Those who assign Acts to the second century understandably also argue that Luke used some of the letters: the consonance between his account of Paul’s movements in chapter 16–21 and the movements and plans in 1 Thessalonians, 1 and 2 Corinthians, and Romans is, in fact, notable. But Galatians, Philippians, and Philemon (leaving aside the disputed letters) don’t fit within the Acts framework, and even when the basic movements recounted in the letters and Acts agree, the many small discrepancies between them argue more for Luke’s independent knowledge than a use of Paul’s letters. Strikingly, Luke never suggests that Paul even wrote letters. In short, Acts is better regarded as an independent source of knowledge about Paul from the late first century, rather than a second-century composition that used his letters in the shaping of its narrative of Paul.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("How reliable is Acts as a historical source, bearing in mind its religious purposes and literary shaping? In general, it is impressively accurate in matters of local detail. Luke knows the correct titles, functions, and times of tenure of local officials. He conveys the local color of different regions. The practices he describes correspond to the practices we know about from other sources."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn56",
      "id": "fn56"
    }
  }, [_vm._v("56")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_32"
    }
  }, [_vm._v("In these respects, Acts is recognizably more grounded in the facts of the first-century Mediterranean world than are the apocryphal Acts. As we shall see in the next chapter, moreover, when Paul’s movements in Acts are compared to those reported in his letters, they provide a reliable if partial framework for reconstructing parts of Paul’s career; we will look also at the two major discrepancies between the sources concerning important events: the decision of the church concerning the gentiles, and Paul’s collection.")])]), _c('a', {
    attrs: {
      "id": "page_32"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_32"
    }
  }, [_vm._v("On the question of historical accuracy, however, three cautionary remarks are necessary. First, the speeches put in Paul’s mouth are entirely Lukan inventions and cannot be taken as evidence for Paul’s own views."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn57",
      "id": "fn57"
    }
  }, [_vm._v("57")]), _vm._v(" Second, Luke’s literary invention places Paul in settings and scenes evocative of Hellenistic models,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn58",
      "id": "fn58"
    }
  }, [_vm._v("58")])]), _vm._v(" and his characterization of Paul is shaped by prophetic imagery drawn from Scripture. Third, we must remember the difference between getting Paul’s world right and recounting his story accurately in every respect. The imaginative evocation of time and place, after all, is a feature of good historical fiction as much as it is of sober historiography. But we must also remember that if history aspires to be something more than a sheer chronicle, it also must employ fictional techniques in the construction of a narrative.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("In sum, Acts is an invaluable secondhand primary source for knowledge of Paul’s life and ministry, but not for his thought, written within decades of Paul’s death, providing an essential narrative framework within which some of Paul’s letters can be located; and even when it diverges on certain points from those letters, it offers a portrait of the apostle that is, in broad terms, historically more plausible than any of the apocryphal writings.")]), _vm._v(" "), _c('p', {
    staticClass: "H2",
    attrs: {
      "id": "s8"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s8"
    }
  }, [_vm._v("Non-Pauline Letters")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Although the opinion that the Letter of James was from the first an anti-Pauline polemic is extreme and indefensible,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn59",
      "id": "fn59"
    }
  }, [_vm._v("59")])]), _vm._v(" a long scholarly tradition "), _c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_vm._v("reads James as a reaction to an “antinomian” tendency within Paulinism, requiring an assertion of the necessity of both faith and works."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn60",
      "id": "fn60"
    }
  }, [_vm._v("60")]), _vm._v(" If such were the case, then James would represent a canonical witness to Pauline controversies after the death of the apostle. But besides erroneously reducing Paul to Galatians and Romans, and James to the thirteen verses where the language of faith and works occurs (2:14–26), careful analysis suggests that the Letter of James is at least as early as Paul’s letters and that James’s language about faith and works does not directly counter Paul’s position. James and Paul should therefore be read independently of each other."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn61",
      "id": "fn61"
    }
  }, [_vm._v("61")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The Second Letter of Peter is certainly a pseudonymous composition and probably the latest composition included in the New Testament canon, though it is not necessary to date it later than the first decade of the second century."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn62",
      "id": "fn62"
    }
  }, [_vm._v("62")])]), _vm._v(" Telling his readers that they should consider the “patience/long-suffering” ("), _c('i', [_vm._v("makrothymia")]), _vm._v(") of the Lord as salvation (2 Pet 3:15), the author continues, “So also our beloved brother Paul wrote to you according to the wisdom given him, speaking of this as he does in all his letters."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn63",
      "id": "fn63"
    }
  }, [_vm._v("63")])]), _vm._v(" There are some things in them hard to understand, which the ignorant and unstable twist to their own destruction, as they do the other scriptures” (3:15–16). The statement is quite remarkable on three points: first, it speaks of “all his letters,” assuming at least some sort of collection; second, it is aware of a tradition of disputed interpretation; third, it implicitly identifies Paul’s letters as having the same authority as “other scriptures” ("), _c('i', [_vm._v("tas loipas graphas")]), _vm._v("). The statement supports the relatively late dating of 2 Peter, but even as it aligns the author with Paul and “all his letters,” it does not offer anything significant to our knowledge of Paul’s life, ministry, or correspondence.")]), _vm._v(" "), _c('p', {
    staticClass: "H2",
    attrs: {
      "id": "s9"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s9"
    }
  }, [_vm._v("Canonical Pauline Letters")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Thirteen letters are attributed to Paul in the New Testament canon: Romans, 1 and 2 Corinthians, Galatians, 1 and 2 Thessalonians, Philippians, Ephesians, Colossians, 1 and 2 Timothy, Titus, and Philemon. "), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_vm._v("The process of canonization did not entirely lack critical discernment."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn64",
      "id": "fn64"
    }
  }, [_vm._v("64")]), _vm._v(" The Letter to the Laodiceans, for example, while by no means heretical, was rejected as spurious, as was the Acts of Paul."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn65",
      "id": "fn65"
    }
  }, [_vm._v("65")])]), _vm._v(" And the Letter to the Hebrews, thought by many to be by Paul, even appearing after Romans in our earliest manuscript of Paul’s letters (P 46), was eventually attributed to one of several candidates other than Paul, precisely on the grounds of style."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn66",
      "id": "fn66"
    }
  }, [_vm._v("66")])]), _vm._v(" It is worth noting that the ancient speakers and readers of Greek who detected such divergence in the case of Hebrews never caviled with respect to any of the other thirteen letters.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("No challenge was put to the Pauline authorship of the canonical collection, in fact, until the nineteenth century. Not even the highly skeptical critics of the Enlightenment, who vigorously disputed the Letter of James, found any reason to challenge the authorship of Colossians, Ephesians, or the Pastorals."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn67",
      "id": "fn67"
    }
  }, [_vm._v("67")])]), _vm._v(" Reformation interpreters, likewise, read all thirteen letters as thoroughly Pauline. Martin Luther was keenly sensitive to the differences between Paul and James, leading him to dismiss the Letter of James from the “proper books” of the New Testament."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn68",
      "id": "fn68"
    }
  }, [_vm._v("68")])]), _vm._v(" But he found no conflict between 1 Timothy and Galatians on the question of the law, commenting on 1 Tim 1:5, “Now these are deep and genuinely Pauline words, and because they are very rich, we must explain them somewhat in order that we may understand it a little and become accustomed to his language.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn69",
      "id": "fn69"
    }
  }, [_vm._v("69")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The challenge to the Pauline authorship of the entire canonical collection began with Friedrich Schleiermacher’s questioning of the authenticity of 1 Timothy—by comparing it to 2 Timothy and Titus!"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn70",
      "id": "fn70"
    }
  }, [_vm._v("70")])]), _vm._v(" Schleiermacher’s critique was quickly extended by J. G. Eichorn, W. M. L. de Wette, and F. C. Baur to include other canonical writings;"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn71",
      "id": "fn71"
    }
  }, [_vm._v("71")])]), _vm._v(" in his monograph on Paul, Baur concluded that only four of the letters ascribed to Paul were actually by him (Romans, 1 and 2 Corinthians, Galatians), while 1 and 2 Thessalonians, "), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }, [_vm._v("Colossians, Ephesians, Philippians, and Philemon, in addition to the Pastorals, were of doubtful authenticity."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn72",
      "id": "fn72"
    }
  }, [_vm._v("72")]), _vm._v(" The floodgates of criticism were open, and despite vigorous responses to the challenge,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn73",
      "id": "fn73"
    }
  }, [_vm._v("73")])]), _vm._v(" scholars even more radical than F. C. Baur challenged the Pauline authorship of all the letters ascribed to him: they all came from the century, but they were all forgeries."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn74",
      "id": "fn74"
    }
  }, [_vm._v("74")])]), _vm._v(" Such a sweeping conclusion left little that could be considered nascent Christianity, and nothing that could be regarded as genuinely Pauline. Bruno Bauer represents an extreme, but his radical position indicates how, once the process of dismantling is begun, it does not have any natural stopping point.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("A compromise position was adopted by more moderate scholars, which enabled them to claim the title of critics while still retaining the Paul that they most treasured: the four letters accepted by F. C. Baur plus Philippians, Philemon, and 1 Thessalonians were accepted as genuine (or “undisputed”); Colossians, Ephesians, and 2 Thessalonians were placed in the “disputed” category; and the Pastorals were definitively excluded from the Pauline corpus. The great authority of H. J. Holtzmann helped secure this compromise position, which continued to represent scholarly orthodoxy to the present time."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn75",
      "id": "fn75"
    }
  }, [_vm._v("75")])]), _vm._v(" Although arguments continued to be made for the authenticity of all Paul’s letters (often by great scholars), the compromise position—so serviceable for the dominant framework of New Testament studies—hardened to what is sometimes called a scholarly consensus, but may more accurately be designated an academic dogma, since the majority of those now holding it have not actually done the hard work of examining and testing the arguments for and against it."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn76",
      "id": "fn76"
    }
  }, [_vm._v("76")])]), _vm._v(" Albert Schweitzer accurately summarized the predictable consequences:")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_vm._v("Not even the most conservative of critics had the boldness to place all the letters which have come down under the name of Paul on a footing of equality. Even those who regarded the Epistles to the Ephesians and Colossians as genuine did not fuse the ideas of these Epistles with the system extracted from the four main Epistles, but presented them separately; and any who "), _c('a', {
    attrs: {
      "id": "page_36"
    }
  }, [_vm._v("were not converted to the rejection of the Pastorals at all events took the precaution to give a separate chapter to the Pauline theology of these writings. If only the personal references might be saved, these Epistles were as completely excluded from the presentation of the Pauline system as if they had been pronounced wholly spurious."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn77",
      "id": "fn77"
    }
  }, [_vm._v("77")])]), _vm._v(" "), _c('p', {
    staticClass: "Body1"
  }, [_vm._v("The criteria for judging a composition Pauline or non-Pauline have not changed since the first challenges by Schleiermacher and Baur: (1) fit within the ministry of Paul as reconstructed from the (uncontested) letters and Acts; (2) style of composition; (3) nature of opponents; (4) organization of the church; and (5) consistency in theme, embracing law and faith, salvation, eschatology, and ethics. At this point in the history of Pauline scholarship, few critics bother examining either the formal or material validity of the criteria, since the “consensus” obviates any such need. But it is important, especially for any adopting a non-consensus position, to assess the strength and weakness of the arguments arranged under these criteria.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Only the first criterion is actually “hard”: if we had a complete account of Paul’s movements, and a letter claimed to come from a time or place contrary to what we knew, then we could disqualify it. But, in fact, we do not have such a complete account of Paul’s movements. Take two examples. First, Acts tells of Pauline captivities in Philippi (16:24–34), Caesarea (23:35–26:32), and Rome (28:16–31), and Paul himself speaks of multiple imprisonments (2 Cor 11:23); but none of these provides a fully satisfying location for even the “undisputed” captivity letters, Philippians and Philemon, so that some scholars have actually invented an Ephesian captivity (for which there is no positive evidence) to support a theory of multiple exchanges between Paul and the Philippians."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn78",
      "id": "fn78"
    }
  }, [_vm._v("78")])]), _vm._v(" Second, the criterion is unevenly applied: neither Acts nor the other letters can provide a compelling date or location for the Letter to the Galatians, yet it is so universally acknowledged as Pauline that the question of its fit within Paul’s ministry is waived; conversely, according to the other criteria usually applied, the “authenticity” of 1 Thessalonians might easily be challenged, but because its internal evidence overlaps so nicely with that in Acts, it is universally accepted as genuine.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The criterion of style can include diction, grammar, syntax, mode of citation, and manner of argumentation. We have seen that the early church decided that Hebrews could not be by Paul on these grounds. "), _c('a', {
    attrs: {
      "id": "page_37"
    }
  }, [_vm._v("Remarkably, no other of the canonical letters was so challenged before Schleiermacher, and he only caviled at a handful of word choices in 1 Timothy. The criterion is notoriously subjective, especially when applied by people who learned Greek laboriously and partially in seminary rather than as a native language. It also pays insufficient attention to four things: (1) the diversity of style even within the undisputed letters (compare Romans to 1 Thessalonians); (2) the influence of subject matter and situation on modes of epistolary discourse; (3) the ancient rhetorical ideal of "), _c('i', [_vm._v("prosōpopoiia")]), _vm._v(" (“writing in character”), which employed distinctive modes for diverse audiences and settings; and (4) the complex character of Pauline compositions (more on this in the next chapter)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn79",
      "id": "fn79"
    }
  }, [_vm._v("79")])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The two criteria of opponents and church order can be dealt with quickly, since both are based on false premises. On the opponents, the Tübingen School saw the authentic Paul as engaged in an ongoing battle against a unified front of “Judaizers” representing James and Peter, whereas the inauthentic letters showed a battle against gnostics of one form or another."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn80",
      "id": "fn80"
    }
  }, [_vm._v("80")])]), _vm._v(" That picture is inadequate. First, in some circumstances it is not clear that Paul is combatting actual living opponents (see Galatians, Colossians) so much as local deviations. Second, greater knowledge of both Jewish Christianity and Gnosticism has complicated the too simple schema adopted by Tübingen and its followers."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn81",
      "id": "fn81"
    }
  }, [_vm._v("81")])]), _vm._v(" As for church order, the classic trajectory from a charismatic community (the authentic Paul) in the first century to an institution (the Pastorals) in the second century that is so confidently sketched by historical critics,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn82",
      "id": "fn82"
    }
  }, [_vm._v("82")])]), _vm._v(" not only is sociologically naive, but fails to reckon with the elements of institution found in the undisputed letters, which are not at all discordant with those found in the Pastorals."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn83",
      "id": "fn83"
    }
  }, [_vm._v("83")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The most frequent criterion applied by contemporary commentators, especially by those who have not thoroughly examined the issue as a whole, is thematic consistency, which can include the topics of salvation, law, faith, eschatology, and ethics."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn84",
      "id": "fn84"
    }
  }, [_vm._v("84")])]), _vm._v(" The disputed composition is said to "), _c('a', {
    attrs: {
      "id": "page_38"
    }
  }, [_vm._v("be divergent in one respect or another from Paul’s usual position in the undisputed letters, and therefore to be reckoned as inauthentic. There is, no doubt, some formal validity to the criterion: if a composition were flatly to deny or distort one such element found in another letter, that would certainly raise questions. If, for example, a putative Pauline letter argued against faith, hope, and love, or if it were to advance the proposition that observance of the law were sufficient for salvation, then serious doubts as to its Pauline origin would be legitimate. The discrepancies identified in the disputed letters fall far short of these obvious cases and represent variations on Pauline themes rather than deviations from them. The premise of absolute consistency in theme is, however, dubious in the case of an apostle who wrote letters in response to local circumstances rather than composed systematic philosophical tractates. In any case, as I have already indicated in my introduction, there is significant diversity on many Pauline themes even within the undisputed letters.")])]), _c('a', {
    attrs: {
      "id": "page_38"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_38"
    }
  }, [_vm._v("In fact, the entire disputation concerning the authorship of Paul’s letters has been haunted by a glaring but unacknowledged error, which can be called the fallacy of false grouping. The seven undisputed letters are formed into a group, to which other letters are compared. The assumption is that the seven letters represent a uniform and consistent Paul in terms of style and theme. The very real differences even among the undisputed letters are ignored for the purpose of establishing such a Pauline “norm” against which deviance can be measured. But, in fact, this norm is abstracted from the specific letters precisely in the interest of providing a foil for comparison. Then this abstraction is compared to another abstraction representing the letters thought to be deviant. Thus, the “Pastorals” as a group are compared to the seven “undisputed letters” as a group, with no recognition of either the differences among the three letters to Paul’s delegates or the differences among the seven undisputed letters."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn85",
      "id": "fn85"
    }
  }, [_vm._v("85")]), _vm._v(" This is simply bad science.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Nevertheless, partly on the basis of such criteria, and partly on the basis of scholarly inertia, the conventional approach to the canonical collection, as we have seen, is to distinguish between disputed and undisputed letters, or, to be more candid, authentic and pseudonymous letters. Seven letters (Romans, Galatians, 1 and 2 Corinthians, 1 Thessalonians, Philippians, Philemon) are taken as authentically Pauline. Three letters (2 Thessalonians, "), _c('a', {
    attrs: {
      "id": "page_39"
    }
  }, [_vm._v("Ephesians, Colossians) are disputed—with some scholars still arguing for their authenticity—but mostly regarded as inauthentic. Three letters (1 Timothy, 2 Timothy, Titus) are designated as inauthentic by all but a tiny minority of critics."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn86",
      "id": "fn86"
    }
  }, [_vm._v("86")]), _vm._v(" The six canonical letters designated as non-Pauline in the strictest sense are recognized as “Pauline” in a weaker sense—that is, as productions of one or more Pauline “schools” operating after the apostle’s death and producing letters in his name. The only evidence for such schools, to be sure, are these very compositions."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn87",
      "id": "fn87"
    }
  }, [_vm._v("87")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Such is the present framework for the scholarly analysis of Paul, a framework that leaves out six of the letters traditionally ascribed to him and contained in the canon. I have already quoted above Schweitzer’s canny observation on the consequences: those letters discarded as non-Pauline are eliminated from theological discussions altogether, since even proponents of a larger Pauline corpus can only engage others on the basis of the reduced body of evidence. The need for a thorough reexamination of the history and the consequences of Pauline scholarship on the issue of authorship is long overdue."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn88",
      "id": "fn88"
    }
  }, [_vm._v("88")])]), _vm._v(" Such a reexamination will need to deal with a set of factors that go beyond the set of explicit “criteria” that I have here identified, and that exercise greater influence than the criteria.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The first factor is the power of construal, which is closely linked to the social construction of ideology."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn89",
      "id": "fn89"
    }
  }, [_vm._v("89")])]), _vm._v(" It is striking that, for nineteen hundred years, readers who assumed Pauline authorship of all the letters were able to make sense of all of them as Pauline, down to and including Enlightenment critics. In similar manner, for two hundred years, the dominant opinion that some letters are inauthentic, reinforced as it is by classroom instruction and scholarly focus, has become a virtually unassailable dogma. Now the burden of showing the “Pauline” character of, say, Colossians falls on the individual reader, who must engage not only the evidence but the great weight of fossilized opinion. "), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_vm._v("The social construction of reality is real, and it is powerfully operative in this matter.")])]), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_vm._v("The second factor is the understandable, if distorting, desire to find a usable Paul. The Holtzmann compromise regarding authorship enabled Protestant scholars to claim the mantle of criticism while at the same time keeping the Paul they most desired, the Paul they considered the defender of righteousness by faith."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn90",
      "id": "fn90"
    }
  }, [_vm._v("90")]), _vm._v(" By the same token, those arguing for the authenticity of the disputed letters are also motivated theologically: it is not a surprise to find such defenders primarily among more conservative scholars who embrace what they consider traditional values concerning gender and social structure. But the quest for an apostle who supports the theology of the scholar is a search that is less than disinterested.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The third factor is the tendency to equate authenticity with authority. If Paul did not write Ephesians, some seem to think, then its value is diminished; if he did not write the Pastorals, they can be dismissed. The tendency is found on both sides of the debate: if liberal scholars seek to deny authenticity in order to save Paul from what they perceive to be distortions, conservative scholars seek to establish authenticity in order to preserve the authority of threatened compositions. But the equation is simply wrong. The letters of Paul gain their authority not from the fact of his having written them but from the fact of their being canonized by the church. For the church, Colossians is read as Scripture whether Paul wrote it or not, just as the Gospel of Mark is read as Scripture even though its authorship remains undetermined. To think otherwise is to displace ecclesial discernment with historical criticism.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("A better and more comprehensive assessment of Paul’s letters must include two elements missing from the standard discussions. The first is the candid admission that the premise of a uniform Pauline “core” is wrong, together with the acknowledgment of multiple clusters within the canonical collection, groups of letters bound together by common style and theme, yet noticeably distinct from other groups. Thus, the Thessalonian letters, the Corinthian letters, Galatians and Romans, Colossians and Ephesians, and the letters to Paul’s delegates (the Pastorals) form five such clusters, with Philippians and Philemon being outliers. These clusters pose a fundamental problem concerning the unity and diversity of the Pauline correspondence. "), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_vm._v("Second is a theory of composition that more adequately accounts for such unity and diversity than the conventional appeal to the passage of time or a theory of development that is theologically driven."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn91",
      "id": "fn91"
    }
  }, [_vm._v("91")]), _vm._v(" I will propose such a theory of composition in chapter 3. In the meantime, there are no sound literary or historical grounds for dismissing any of the canonical letters as inauthentic, and every reason to attend to each of them as a source for Paul’s life and thought.")]), _vm._v(" "), _c('p', {
    staticClass: "line"
  }, [_c('img', {
    attrs: {
      "alt": "line",
      "src": "images/line1.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn1",
      "id": "rfn1"
    }
  }, [_vm._v("1")]), _vm._v(". For Jesus, see Josephus, "), _c('i', [_vm._v("Antiquities of the Jews")]), _vm._v(" 18.3.3; for James, "), _c('i', [_vm._v("Antiquities of the Jews")]), _vm._v(" 20.9.1.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn2",
      "id": "rfn2"
    }
  }, [_vm._v("2")]), _vm._v(". It is remarkable both how common and how little noticed this basic fallacy is. The fact that Paul dominates the second portion of the Acts narrative, that he has thirteen letters ascribed to him, and that he is mentioned by 2 Peter 3:15–16 does not justify the position (central since at least the time of F. C. Baur) that Paul was the pivot around which everything in nascent Christianity turned.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn3",
      "id": "rfn3"
    }
  }, [_vm._v("3")]), _vm._v(". It is James’s death that is recounted in Josephus, "), _c('i', [_vm._v("Antiquities of the Jews")]), _vm._v(" 20.9.1. Eusebius reports inconsistent accounts of the martyrdom from Clement of Alexandria ("), _c('i', [_vm._v("Ecclesiastical History")]), _vm._v(" 2.1.5) and Hegesippus ("), _c('i', [_vm._v("Ecclesiastical History")]), _vm._v(" 3.23.10–18). Even more complexity is added by the account in Pseudo-Clementine "), _c('i', [_vm._v("Recognitions")]), _vm._v(" 1.70–71, by the Manichaean "), _c('i', [_vm._v("Psalms of Heracleides")]), _vm._v(" 192.8–9 and "), _c('i', [_vm._v("Psalms of Sarakoth")]), _vm._v(" 142.25–26, and by the gnostic tractate "), _c('i', [_vm._v("Second Revelation of James")]), _vm._v(" 61–62.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn4",
      "id": "rfn4"
    }
  }, [_vm._v("4")]), _vm._v(". See Eusebius’s citations from Clement of Alexandria’s "), _c('i', [_vm._v("Hypotyposes")]), _vm._v(", book 6, in "), _c('i', [_vm._v("HE")]), _vm._v(" 2.1–3.7, and Hegesippus in "), _c('i', [_vm._v("HE")]), _vm._v(" 2.23.4, as well as his own statement that “James was first elected to the throne of the bishopric of the church in Jerusalem” ("), _c('i', [_vm._v("HE")]), _vm._v(" 2.1.2).")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn5",
      "id": "rfn5"
    }
  }, [_vm._v("5")]), _vm._v(". See the gnostic "), _c('i', [_vm._v("Gospel of Thomas")]), _vm._v(" 12; "), _c('i', [_vm._v("Secret Book of James")]), _vm._v("; "), _c('i', [_vm._v("First Revelation of James")]), _vm._v("; and "), _c('i', [_vm._v("Second Revelation of James.")])]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn6",
      "id": "rfn6"
    }
  }, [_vm._v("6")]), _vm._v(". For this complex literature, see L. Johnson, "), _c('i', [_vm._v("Letter of James")]), _vm._v(", 102–6, and for its connection to Paul, see below.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn7",
      "id": "rfn7"
    }
  }, [_vm._v("7")]), _vm._v(". English translation by Lake, "), _c('i', [_vm._v("Apostolic Fathers")]), _vm._v(", 17.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn8",
      "id": "rfn8"
    }
  }, [_vm._v("8")]), _vm._v(". Without explicit citation but with unmistakable allusion, Clement’s letter also reveals knowledge of the Letter to the Hebrews and the Letter of James; see L. Johnson, “Reception of James.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn9",
      "id": "rfn9"
    }
  }, [_vm._v("9")]), _vm._v(". Translation by Lake, "), _c('i', [_vm._v("Apostolic Fathers")]), _vm._v(", 187. There are possible allusions or echoes to these letters in Ignatius: 1 Cor 1:20 (Ign. "), _c('i', [_vm._v("Eph.")]), _vm._v(" 18.1); 1 Cor 2:10 (Ign. "), _c('i', [_vm._v("Phil.")]), _vm._v(" 7.1); 1 Cor 6:9 (Ign. "), _c('i', [_vm._v("Phil.")]), _vm._v(" 3.3; "), _c('i', [_vm._v("Eph.")]), _vm._v(" 16.1); 1 Cor 3:16 (Ign. "), _c('i', [_vm._v("Eph.")]), _vm._v(" 15.2); 1 Cor 5:7 (Ign. "), _c('i', [_vm._v("Magn.")]), _vm._v(" 10.2); 1 Cor 9:27 (Ign. "), _c('i', [_vm._v("Trall.")]), _vm._v(" 12.2); 1 Cor 10:16–17 (Ign. "), _c('i', [_vm._v("Phil.")]), _vm._v(" 4.1); 1 Cor 15:8–9 (Ign. "), _c('i', [_vm._v("Rom.")]), _vm._v(" 9.2); 1 Cor 15:12 (Ign. "), _c('i', [_vm._v("Trall.")]), _vm._v(" 9.2); Rom 1:3 (Ign. "), _c('i', [_vm._v("Eph.")]), _vm._v(" 18.2, 20.2; "), _c('i', [_vm._v("Rom.")]), _vm._v(" 7.2; "), _c('i', [_vm._v("Smyrn.")]), _vm._v(" 1.1); Phil 4:13 (Ign. "), _c('i', [_vm._v("Smyrn.")]), _vm._v(" 4.2); Eph 2:16 (Ign. "), _c('i', [_vm._v("Smyrn.")]), _vm._v(" 1.2); Eph 4:2 (Ign. "), _c('i', [_vm._v("Pol.")]), _vm._v(" 1.2); Eph 5:25 (Ign. "), _c('i', [_vm._v("Pol.")]), _vm._v(" 5.1).")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn10",
      "id": "rfn10"
    }
  }, [_vm._v("10")]), _vm._v(". Translation by Lake, "), _c('i', [_vm._v("Apostolic Fathers")]), _vm._v(", 287.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn11",
      "id": "rfn11"
    }
  }, [_vm._v("11")]), _vm._v(". The best ancient source for Marcion and his teachings is Tertullian, "), _c('i', [_vm._v("Against Marcion")]), _vm._v(", written ca. 212; English text available in "), _c('i', [_vm._v("ANF")]), _vm._v(" 3:269–475; the classic study is by Harnack, "), _c('i', [_vm._v("Marcion")]), _vm._v("; see also Lieu, "), _c('i', [_vm._v("Marcion")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn12",
      "id": "rfn12"
    }
  }, [_vm._v("12")]), _vm._v(". Tertullian, "), _c('i', [_vm._v("Against Marcion")]), _vm._v(" 5.21.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn13",
      "id": "rfn13"
    }
  }, [_vm._v("13")]), _vm._v(". See Meeks and Fitzgerald, "), _c('i', [_vm._v("Writings of St. Paul")]), _vm._v(", 286–88.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn14",
      "id": "rfn14"
    }
  }, [_vm._v("14")]), _vm._v(". For a sample, see Meeks and Fitzgerald, "), _c('i', [_vm._v("Writings of St. Paul")]), _vm._v(", 272–83.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn15",
      "id": "rfn15"
    }
  }, [_vm._v("15")]), _vm._v(". Irenaeus of Lyons, "), _c('i', [_vm._v("Against Heresies")]), _vm._v("; English translation in "), _c('i', [_vm._v("ANF")]), _vm._v(" 1:315–567.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn16",
      "id": "rfn16"
    }
  }, [_vm._v("16")]), _vm._v(". Text in J. K. Elliott, "), _c('i', [_vm._v("Apocryphal New Testament")]), _vm._v(", 576–77.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn17",
      "id": "rfn17"
    }
  }, [_vm._v("17")]), _vm._v(". Text in J. K. Elliott, "), _c('i', [_vm._v("Apocryphal New Testament")]), _vm._v(", 399–401.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn18",
      "id": "rfn18"
    }
  }, [_vm._v("18")]), _vm._v(". Clement of Alexandria, "), _c('i', [_vm._v("Stromateis")]), _vm._v(" 3.53; Clement also passes on a tradition that has Paul making use of the "), _c('i', [_vm._v("Sibylline Oracles")]), _vm._v(" in "), _c('i', [_vm._v("Stromateis")]), _vm._v(" 6.5; for text, see Meeks and Fitzgerald, "), _c('i', [_vm._v("Writings of St. Paul")]), _vm._v(", 216.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn19",
      "id": "rfn19"
    }
  }, [_vm._v("19")]), _vm._v(". For the short excerpt from Ambrosiaster’s "), _c('i', [_vm._v("Commentary on 2 Corinthians")]), _vm._v(", see Meeks and Fitzgerald, "), _c('i', [_vm._v("Writings of St. Paul")]), _vm._v(", 213.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn20",
      "id": "rfn20"
    }
  }, [_vm._v("20")]), _vm._v(". John Chrysostom, "), _c('i', [_vm._v("In Praise of Saint Paul")]), _vm._v(", translated and analyzed by Mitchell, "), _c('i', [_vm._v("Heavenly Trumpet")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn21",
      "id": "rfn21"
    }
  }, [_vm._v("21")]), _vm._v(". For text, see Meeks and Fitzgerald, "), _c('i', [_vm._v("Writings of St. Paul")]), _vm._v(", 210–11.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn22",
      "id": "rfn22"
    }
  }, [_vm._v("22")]), _vm._v(". The earliest was Celsus (late second century) in his "), _c('i', [_vm._v("True Word")]), _vm._v(" (to which Origen responded with "), _c('i', [_vm._v("Against Celsus")]), _vm._v("), although he did not make Paul his particular target—apart from mocking the Christians’ preferring foolishness to wisdom (see 1 Cor 1:18–25). Paul is more of a target in the fourth-century attacks by Julian, "), _c('i', [_vm._v("Against the Galileans")]), _vm._v(" 106 a–c, 327 a–b, and Porphyry, "), _c('i', [_vm._v("Against the Christians")]), _vm._v("; for excerpts in translation, see Meeks and Fitzgerald, "), _c('i', [_vm._v("Writings of St. Paul")]), _vm._v(", 265–71.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn23",
      "id": "rfn23"
    }
  }, [_vm._v("23")]), _vm._v(". Contra Martin Hengel’s attempt to make it so in “Der Jakobusbriefe” (translation in Meeks and Fitzgerald, "), _c('i', [_vm._v("Writings of St. Paul")]), _vm._v(", 242–53), a position rejected even by the earliest Tübingen critics; see, e.g., Kern, "), _c('i', [_vm._v("Der Charakter und Ursprung des Briefes Jacobi")]), _vm._v("; see the discussion in L. Johnson, "), _c('i', [_vm._v("Letter of James")]), _vm._v(", 58–63, 143–55.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn24",
      "id": "rfn24"
    }
  }, [_vm._v("24")]), _vm._v(". See "), _c('i', [_vm._v("Panarion")]), _vm._v(" 28.5.1–3, 30.16.6–9; translation in Meeks and Fitzgerald, "), _c('i', [_vm._v("Writings of St. Paul")]), _vm._v(", 229–30.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn25",
      "id": "rfn25"
    }
  }, [_vm._v("25")]), _vm._v(". For the complex composition, see L. Johnson, "), _c('i', [_vm._v("Letter of James")]), _vm._v(", 103–5.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn26",
      "id": "rfn26"
    }
  }, [_vm._v("26")]), _vm._v(". See F. Jones, “Pseudo-Clementines.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn27",
      "id": "rfn27"
    }
  }, [_vm._v("27")]), _vm._v(". See "), _c('i', [_vm._v("Recognitions")]), _vm._v(" 1.33.3–69.8, 1.70.1–71.6.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn28",
      "id": "rfn28"
    }
  }, [_vm._v("28")]), _vm._v(". For English translation and analysis, see Van Voorst, "), _c('i', [_vm._v("Ascents of James")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn29",
      "id": "rfn29"
    }
  }, [_vm._v("29")]), _vm._v(". See, e.g., Strecker, "), _c('i', [_vm._v("Das Judenchristentum in den Pseudoklementinen")]), _vm._v(", 187–96.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn30",
      "id": "rfn30"
    }
  }, [_vm._v("30")]), _vm._v(". For the interpretation of the passage in the Greek patristic tradition, see Wallace, "), _c('i', [_vm._v("Snatched into Paradise")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn31",
      "id": "rfn31"
    }
  }, [_vm._v("31")]), _vm._v(". Translation in J. K. Elliott, "), _c('i', [_vm._v("Apocryphal New Testament")]), _vm._v(", 616–44.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn32",
      "id": "rfn32"
    }
  }, [_vm._v("32")]), _vm._v(". See Henning, "), _c('i', [_vm._v("Educating Early Christians")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn33",
      "id": "rfn33"
    }
  }, [_vm._v("33")]), _vm._v(". Introduction and translation by George W. MacRae and William R. Murdock in J. M. Robinson, "), _c('i', [_vm._v("Nag Hammadi Library in English")]), _vm._v(", 239–41.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn34",
      "id": "rfn34"
    }
  }, [_vm._v("34")]), _vm._v(". In "), _c('i', [_vm._v("On Baptism")]), _vm._v(" 17 (ca. 180), Tertullian says it was written by a presbyter in Asia who (on the basis of Thecla) advocated ministry for women. The other compositions devoted to the deeds of the apostles are the Acts of Thomas, the Acts of Peter, the Acts of Andrew, and the Acts of John. For translation of the Acts of Paul, see J. K. Elliott, "), _c('i', [_vm._v("Apocryphal New Testament")]), _vm._v(", 350–89; for discussion of the religious sensibility found in the several Acts, see L. Johnson, "), _c('i', [_vm._v("Among the Gentiles")]), _vm._v(", 172–83, 365–71.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn35",
      "id": "rfn35"
    }
  }, [_vm._v("35")]), _vm._v(". See the analysis by J. K. Elliott, "), _c('i', [_vm._v("Apocryphal New Testament")]), _vm._v(", 350–57.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn36",
      "id": "rfn36"
    }
  }, [_vm._v("36")]), _vm._v(". See Thomas, "), _c('i', [_vm._v("Acts of Peter")]), _vm._v(". To be sure, the canonical Acts also has some traits of the ancient novel; see Pervo, "), _c('i', [_vm._v("Profit with Delight")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn37",
      "id": "rfn37"
    }
  }, [_vm._v("37")]), _vm._v(". See Malherbe, “Physical Description of Paul.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn38",
      "id": "rfn38"
    }
  }, [_vm._v("38")]), _vm._v(". See, e.g., D. MacDonald, “Virgins, Widows, and Paul”; S. Davies, "), _c('i', [_vm._v("Revolt of the Widows")]), _vm._v("; Bassler, “Widow’s Tale.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn39",
      "id": "rfn39"
    }
  }, [_vm._v("39")]), _vm._v(". I take up the question in L. Johnson, "), _c('i', [_vm._v("First and Second Letters to Timothy")]), _vm._v(", 259–76.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn40",
      "id": "rfn40"
    }
  }, [_vm._v("40")]), _vm._v(". See especially the nuanced and responsible approach of Hylen, "), _c('i', [_vm._v("Modest Apostle")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn41",
      "id": "rfn41"
    }
  }, [_vm._v("41")]), _vm._v(". Translation and discussion by Concannon, “Acts of Timothy.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn42",
      "id": "rfn42"
    }
  }, [_vm._v("42")]), _vm._v(". Translation and discussion by Pervo, “Acts of Titus.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn43",
      "id": "rfn43"
    }
  }, [_vm._v("43")]), _vm._v(". Translation and discussion by Eastman, “Epistle of Pseudo-Dionysius.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn44",
      "id": "rfn44"
    }
  }, [_vm._v("44")]), _vm._v(". Translation and discussion in J. K. Elliott, "), _c('i', [_vm._v("Apocryphal New Testament")]), _vm._v(", 543–46.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn45",
      "id": "rfn45"
    }
  }, [_vm._v("45")]), _vm._v(". E.g., Gal 1:3, 11; 6:18; Phil 1:2, 3, 12, 13, 18, 19–20, 21; 2:2, 12, 13, 14; 3:1; 4:6, 8, 9, 22, 23.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn46",
      "id": "rfn46"
    }
  }, [_vm._v("46")]), _vm._v(". Translation and discussion in J. K. Elliott, "), _c('i', [_vm._v("Apocryphal New Testament")]), _vm._v(", 547–53.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn47",
      "id": "rfn47"
    }
  }, [_vm._v("47")]), _vm._v(". See Malherbe, “ ‘Seneca’ on Paul as Letter Writer.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn48",
      "id": "rfn48"
    }
  }, [_vm._v("48")]), _vm._v(". The designation took hold especially because of the pioneering work of Cadbury, "), _c('i', [_vm._v("Making of Luke-Acts")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn49",
      "id": "rfn49"
    }
  }, [_vm._v("49")]), _vm._v(". For orientation to Luke-Acts, see in particular the introductions to L. Johnson, "), _c('i', [_vm._v("Gospel of Luke")]), _vm._v(", and "), _c('i', [_vm._v("Acts of the Apostles")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn50",
      "id": "rfn50"
    }
  }, [_vm._v("50")]), _vm._v(". L. Johnson, "), _c('i', [_vm._v("Prophetic Jesus, Prophetic Church")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn51",
      "id": "rfn51"
    }
  }, [_vm._v("51")]), _vm._v(". A second-century dating for Acts has been advanced by, among others, Pervo, "), _c('i', [_vm._v("Dating Acts")]), _vm._v(", but I find the reasons given for that dating unpersuasive.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn52",
      "id": "rfn52"
    }
  }, [_vm._v("52")]), _vm._v(". See Sterling, "), _c('i', [_vm._v("Historiography and Self-Definition")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn53",
      "id": "rfn53"
    }
  }, [_vm._v("53")]), _vm._v(". Contemporary critics are fully aware of the biases displayed in Josephus’s "), _c('i', [_vm._v("Against Apion")]), _vm._v(", "), _c('i', [_vm._v("Jewish War")]), _vm._v(", and "), _c('i', [_vm._v("Antiquities of the Jews")]), _vm._v(" (written between 75 and 100 CE), while still regarding these compositions as indispensable sources for our knowledge of Judaism in the first century CE.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn54",
      "id": "rfn54"
    }
  }, [_vm._v("54")]), _vm._v(". See Dupont, "), _c('i', [_vm._v("Sources of Acts")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn55",
      "id": "rfn55"
    }
  }, [_vm._v("55")]), _vm._v(". The shift from third-person to first-person narration in the so-called we passages suggests, but does not prove, the participation of an eyewitness; see Cadbury, “ ‘We’ and ‘I’ Passages”; Praeder, “Problem of First-Person Narration.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn56",
      "id": "rfn56"
    }
  }, [_vm._v("56")]), _vm._v(". See esp. Marshall, "), _c('i', [_vm._v("Luke")]), _vm._v("; Hemer, "), _c('i', [_vm._v("Book of Acts")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn57",
      "id": "rfn57"
    }
  }, [_vm._v("57")]), _vm._v(". See Dibelius, “Speeches in Acts”; L. Johnson, "), _c('i', [_vm._v("Septuagintal Midrash")]), _vm._v("; Wason, “ ‘All Things to All People.’ ”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn58",
      "id": "rfn58"
    }
  }, [_vm._v("58")]), _vm._v(". At Lystra, Paul and Barnabas are taken to be Hermes and Zeus (Acts 14:8–18), a scene that recalls Ovid’s tale of Baucis and Philemon ("), _c('i', [_vm._v("Metamorphoses")]), _vm._v(" 8:613–738); Paul’s speech in Athens echoes Plato’s and Xenophon’s accounts of the trial of Socrates ("), _c('i', [_vm._v("Apology")]), _vm._v("); Paul’s reception by the natives of Malta recalls a similar scene in Dio Chrysostom’s "), _c('i', [_vm._v("Euboean Discourse (Oration 7).")])]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn59",
      "id": "rfn59"
    }
  }, [_vm._v("59")]), _vm._v(". See Hengel, “Der Jakobusbriefe.” As Joseph B. Mayor sagely observes, given their respective positions in the first century, it is just as logically possible to imagine that Paul was responding to James! See Mayor, "), _c('i', [_vm._v("Epistle of Saint James")]), _vm._v(", xci–cii, clxxxiii–clxxxviii.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn60",
      "id": "rfn60"
    }
  }, [_vm._v("60")]), _vm._v(". See the succinct statement of the position by Jeremias, “Paul and James.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn61",
      "id": "rfn61"
    }
  }, [_vm._v("61")]), _vm._v(". For full discussion, see L. Johnson, "), _c('i', [_vm._v("Letter of James")]), _vm._v(", 58–63, 111–14, 156.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn62",
      "id": "rfn62"
    }
  }, [_vm._v("62")]), _vm._v(". See L. Johnson, "), _c('i', [_vm._v("Writings of the New Testament")]), _vm._v(", 437–47.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn63",
      "id": "rfn63"
    }
  }, [_vm._v("63")]), _vm._v(". Although this is not entirely correct, Paul does speak of "), _c('i', [_vm._v("makrothymia")]), _vm._v(" in Rom 2:4; 9:22; 2 Cor 6:6; Gal 5:22; Eph 4:2; Col 1:11; 3:12; 1 Tim 1:16; 2 Tim 3:10; 4:2.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn64",
      "id": "rfn64"
    }
  }, [_vm._v("64")]), _vm._v(". For the process, see Mitton, "), _c('i', [_vm._v("Pauline Corpus")]), _vm._v("; Trobisch, "), _c('i', [_vm._v("Paul’s Letter Collection")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn65",
      "id": "rfn65"
    }
  }, [_vm._v("65")]), _vm._v(". See the Muratorian Canon and other documents pertinent to canonization conveniently gathered in Holladay, "), _c('i', [_vm._v("Introduction to the New Testament")]), _vm._v(", 941–51.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn66",
      "id": "rfn66"
    }
  }, [_vm._v("66")]), _vm._v(". See Eusebius, "), _c('i', [_vm._v("Ecclesiastical History")]), _vm._v(" 6.25.3–14.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn67",
      "id": "rfn67"
    }
  }, [_vm._v("67")]), _vm._v(". See the review of patristic and medieval interpretation of the Pastorals in L. Johnson, "), _c('i', [_vm._v("Letters to Timothy")]), _vm._v(", 26–35.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn68",
      "id": "rfn68"
    }
  }, [_vm._v("68")]), _vm._v(". Luther, “Preface to the New Testament (1522).”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn69",
      "id": "rfn69"
    }
  }, [_vm._v("69")]), _vm._v(". Luther, “Sermons on the Sum of Christian Life.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn70",
      "id": "rfn70"
    }
  }, [_vm._v("70")]), _vm._v(". Schleiermacher, "), _c('i', [_vm._v("Über den sogenannten Ersten Brief des Paulos")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn71",
      "id": "rfn71"
    }
  }, [_vm._v("71")]), _vm._v(". Eichhorn, "), _c('i', [_vm._v("Einleitung in das Neue Testament")]), _vm._v("; de Wette, "), _c('i', [_vm._v("Historico-Critical Introduction")]), _vm._v(", 298–304; Baur, "), _c('i', [_vm._v("Die sogennanten Pastoralbriefe")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn72",
      "id": "rfn72"
    }
  }, [_vm._v("72")]), _vm._v(". Baur, "), _c('i', [_vm._v("Paulus, der Apostel Jesu Christi")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn73",
      "id": "rfn73"
    }
  }, [_vm._v("73")]), _vm._v(". Most impressively by Wiesinger, "), _c('i', [_vm._v("Biblical Commentary")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn74",
      "id": "rfn74"
    }
  }, [_vm._v("74")]), _vm._v(". Above all, see B. Bauer, "), _c('i', [_vm._v("Kritik der paulinischen Briefe")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn75",
      "id": "rfn75"
    }
  }, [_vm._v("75")]), _vm._v(". Holtzmann, "), _c('i', [_vm._v("Lehrbuch der historisch-kritisch Einleitung")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn76",
      "id": "rfn76"
    }
  }, [_vm._v("76")]), _vm._v(". The dogmatism of this statement by Jack T. Sanders is not exceptional: “That Colossians, Ephesians, 2 Thessalonians, 1 and 2 Timothy, Titus, and 1 Peter are pseudonymous and imitate Paul’s style and thought is not to be debated here but rather accepted as the assured result of critical historical scholarship.” "), _c('i', [_vm._v("Ethics in the New Testament")]), _vm._v(", 67.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn77",
      "id": "rfn77"
    }
  }, [_vm._v("77")]), _vm._v(". Schweitzer, "), _c('i', [_vm._v("Paul and His Interpreters")]), _vm._v(", 27.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn78",
      "id": "rfn78"
    }
  }, [_vm._v("78")]), _vm._v(". See, e.g., Duncan, “Paul’s Ministry in Asia.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn79",
      "id": "rfn79"
    }
  }, [_vm._v("79")]), _vm._v(". For more on this, see L. Johnson, "), _c('i', [_vm._v("Letters to Timothy")]), _vm._v(", 68–72.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn80",
      "id": "rfn80"
    }
  }, [_vm._v("80")]), _vm._v(". For a sampling, see Baur, "), _c('i', [_vm._v("Die sogennanten Pastoralbriefe")]), _vm._v("; Mangold, "), _c('i', [_vm._v("Die Irrlehrer der Pastoralbriefe")]), _vm._v("; Georgi, "), _c('i', [_vm._v("Opponents of Paul")]), _vm._v("; Jewett, “Agitators and the Galatian Community” and “Conflicting Movements”; Meeks and Francis, "), _c('i', [_vm._v("Conflict at Colossae")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn81",
      "id": "rfn81"
    }
  }, [_vm._v("81")]), _vm._v(". On Jewish Christianity, see R. Brown, “Not Jewish Christianity.” On the complexities of “Gnosticism,” see Bianchi, "), _c('i', [_vm._v("Le Origini dello Gnosticismo")]), _vm._v("; Layton, "), _c('i', [_vm._v("Rediscovery of Gnosticism")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn82",
      "id": "rfn82"
    }
  }, [_vm._v("82")]), _vm._v(". See Campenhausen, "), _c('i', [_vm._v("Ecclesiastical Authority")]), _vm._v("; Brosch, "), _c('i', [_vm._v("Charismen und Ämter")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn83",
      "id": "rfn83"
    }
  }, [_vm._v("83")]), _vm._v(". See L. Johnson, “Rise of Church Order.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn84",
      "id": "rfn84"
    }
  }, [_vm._v("84")]), _vm._v(". A sample: Mitton, "), _c('i', [_vm._v("Ephesians")]), _vm._v("; Best, "), _c('i', [_vm._v("Epistle to the Ephesians")]), _vm._v("; Lohse, "), _c('i', [_vm._v("Colossians and Philemon")]), _vm._v("; Schweizer, "), _c('i', [_vm._v("Letter to the Colossians")]), _vm._v("; M. MacDonald, "), _c('i', [_vm._v("Colossians and Ephesians")]), _vm._v("; Dibelius and Conzelmann, "), _c('i', [_vm._v("Pastoral Epistles")]), _vm._v("; Bassler, "), _c('i', [_vm._v("1 Timothy, 2 Timothy, Titus")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn85",
      "id": "rfn85"
    }
  }, [_vm._v("85")]), _vm._v(". See L. Johnson, "), _c('i', [_vm._v("Letters to Timothy")]), _vm._v(", 63–64.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn86",
      "id": "rfn86"
    }
  }, [_vm._v("86")]), _vm._v(". The best place to see the dominant position—as well as the weak arguments adduced in its favor—is introductions to the New Testament; see, e.g., Kummel, "), _c('i', [_vm._v("Introduction to the New Testament")]), _vm._v("; Marxsen, "), _c('i', [_vm._v("Introduction to the New Testament")]), _vm._v("; Koester, "), _c('i', [_vm._v("Introduction to the New Testament")]), _vm._v("; R. Brown, "), _c('i', [_vm._v("Introduction to the New Testament")]), _vm._v("; Holladay, "), _c('i', [_vm._v("Introduction to the New Testament")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn87",
      "id": "rfn87"
    }
  }, [_vm._v("87")]), _vm._v(". For discussion, see R. Collins, "), _c('i', [_vm._v("Letters That Paul Did Not Write")]), _vm._v("; and on the larger issue of Christian pseudonymity, see Aland, “Problem of Anonymity”; Meade, "), _c('i', [_vm._v("Pseudonymity and Canon")]), _vm._v("; Metzger, “Literary Forgeries.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn88",
      "id": "rfn88"
    }
  }, [_vm._v("88")]), _vm._v(". I have attempted this with respect to the Pastoral Letters in L. Johnson, "), _c('i', [_vm._v("Letters to Timothy")]), _vm._v(", 20–102.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn89",
      "id": "rfn89"
    }
  }, [_vm._v("89")]), _vm._v(". See Berger and Luckmann, "), _c('i', [_vm._v("Social Construction of Reality")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn90",
      "id": "rfn90"
    }
  }, [_vm._v("90")]), _vm._v(". It is to the credit of Holtzmann, "), _c('i', [_vm._v("Die Pastoralbriefe")]), _vm._v(", that, while devoting 282 pages to the question of inauthenticity, he recognizes the specific Protestant theological agenda involved in this sort of historical criticism (see 280–82).")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn91",
      "id": "rfn91"
    }
  }, [_vm._v("91")]), _vm._v(". Campenhausen makes it clear: “In the course of these three centuries the ideal to which Christianity had originally been committed was impaired in various ways: not only do we find rigidities in attitude, curtailment of aspiration, distortion of insight, but also in every department—and indisputable trivialization” ("), _c('i', [_vm._v("Ecclesiastical Authority")]), _vm._v(", 3). At one end of this development, Campenhausen places “Paul,” and at the other end, the “Pastorals” (301).")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-3a8ba322","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/chapter001.vue
// module id = 78
// module chunks = 0